import type { DateTime } from "luxon";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDraftMode } from "../contexts/DraftModeContext";
import { trackEvent } from "../utils/analytics";
import { type NurseInShift, NurseTrainingStatus, type WeekData } from "../utils/models";
import { toIsoDate } from "../utils/scheduleWeeks";
import { useErrorSnackbar } from "./useErrorSnackbar";
import { useHandleNurseChange } from "./useHandleNurseChange";
import { useScheduleSaves } from "./useScheduleSaves";
import { type ShiftDayWeeks, useShiftDayWeeks } from "./useScheduleWeeks";

/** The training statuses a shift can be flagged with. They are mutually exclusive; `Regular` clears them. */
export type ShiftTrainingFlag = NurseTrainingStatus.Trainee | NurseTrainingStatus.Orientation | NurseTrainingStatus.Education;

/** The shift to flag and the schedule day it is listed on (`scheduleDayOf`), which every staffing write is keyed by. */
export type ShiftFlagTarget = {
	nurseInShift: NurseInShift;
	date: DateTime;
	shiftType: string;
	/** Passed through to the mutations untouched: they treat `undefined` as the scheduled view. */
	showActual: boolean | undefined;
	/** Surface name sent as the events' `source`, e.g. `"nurse_menu"`. */
	analyticsSource: string;
};

type NurseChange = ReturnType<typeof useHandleNurseChange>;

// The actual view's training events carry this suffix on their source; the charge event never did.
const ACTUAL_VIEW_SOURCE_SUFFIX = "_actual";
const ANALYTICS_DATE_FORMAT = "MM/DD/yyyy";

const flagEventProperties = ({ source, nurseId, shiftType, date }: { source: string; nurseId: number; shiftType: string; date: DateTime }) => ({
	source,
	nurse_id: nurseId,
	shift_type: shiftType,
	date: date.toFormat(ANALYTICS_DATE_FORMAT),
});

// The Mixpanel event for a training-status change: the NEW status is checked first, then the current
// one. The trainee names read inverted, but they are the shipped event names — kept verbatim so the
// event history stays continuous.
const trainingStatusEventName = (current: NurseTrainingStatus, next: NurseTrainingStatus): string => {
	if (next === NurseTrainingStatus.Education) return "Staffing - mark education";
	if (current === NurseTrainingStatus.Education) return "Staffing - remove education";
	if (current === NurseTrainingStatus.Trainee) return "Staffing - mark trainee";
	return "Staffing - remove trainee";
};

// A training status belongs to the nurse's whole day, so it is written to every shift she has that
// day across all shift types, scheduled and actual alike. Each row's day is found by its date.
const collectNurseDayShifts = (weeks: WeekData[], nurseInShift: NurseInShift, date: DateTime, showActual: boolean | undefined) => {
	const isoDate = toIsoDate(date);
	const days = weeks.flatMap((week) => week.weeklyShifts).flatMap((row) => row.days.filter((day) => toIsoDate(day.date) === isoDate));
	const foundActual = days.flatMap((day) => day.nursesActual?.filter((nurse) => nurse.nurseId === nurseInShift.nurseId) ?? []);
	// When in actual view and nursesActual lookup misses (e.g., unscheduled actual),
	// use nurseInShift directly — it IS the actual shift.
	const shiftsActual: NurseInShift[] = showActual && foundActual.length === 0 ? [nurseInShift] : foundActual;
	const shifts: NurseInShift[] = days.flatMap((day) => day.nurses.filter((nurse) => nurse.nurseId === nurseInShift.nurseId));
	return { shifts, shiftsActual };
};

const useChargeToggle = (
	{ nurseInShift, date, shiftType, showActual, analyticsSource }: ShiftFlagTarget,
	toggleNurseCharge: NurseChange["toggleNurseCharge"],
	isSaving: boolean,
) => {
	const showErrorSnackbar = useErrorSnackbar();
	const { updateNurseChargeStatus } = useScheduleSaves();
	const { isDraftMode } = useDraftMode();

	return useCallback(async (): Promise<boolean> => {
		if (isSaving) return false;

		const properties = flagEventProperties({ source: analyticsSource, nurseId: nurseInShift.nurseId, shiftType, date });
		trackEvent(nurseInShift.charge ? "Staffing - remove charge" : "Staffing - make charge", properties);

		try {
			await toggleNurseCharge({ nurse: { ...nurseInShift, shiftType }, date, shiftType, isActual: showActual });
		} catch (e) {
			showErrorSnackbar("Failed to update charge status", { nurseId: nurseInShift.id }, e);
			return false;
		}

		updateNurseChargeStatus(nurseInShift.id, date, shiftType, !nurseInShift.charge, isDraftMode);
		return true;
	}, [isSaving, analyticsSource, nurseInShift, shiftType, date, toggleNurseCharge, showActual, showErrorSnackbar, updateNurseChargeStatus, isDraftMode]);
};

const useTrainingStatusSetter = (
	{ nurseInShift, date, shiftType, showActual, analyticsSource }: ShiftFlagTarget,
	loadWeeks: ShiftDayWeeks["loadWeeks"],
	setNurseTrainingStatus: NurseChange["setNurseTrainingStatus"],
	isSaving: boolean,
) => {
	const { t } = useTranslation();
	const showErrorSnackbar = useErrorSnackbar();
	const { updateNurseDayTrainingStatus } = useScheduleSaves();
	const { isDraftMode } = useDraftMode();

	return useCallback(
		async (newTrainingStatus: NurseTrainingStatus): Promise<boolean> => {
			if (isSaving) return false;

			// The status is written to all of the nurse's shifts that day, read from the day's weeks once clicked.
			let weeks: WeekData[];
			try {
				weeks = await loadWeeks();
			} catch (e) {
				showErrorSnackbar(t("staffing.changes.schedule.load.failed"), { nurseId: nurseInShift.id, date: toIsoDate(date) }, e);
				return false;
			}

			const source = showActual ? `${analyticsSource}${ACTUAL_VIEW_SOURCE_SUFFIX}` : analyticsSource;
			const properties = flagEventProperties({ source, nurseId: nurseInShift.nurseId, shiftType, date });
			trackEvent(trainingStatusEventName(nurseInShift.trainingStatus, newTrainingStatus), { ...properties, training_status: newTrainingStatus });

			try {
				const { shifts, shiftsActual } = collectNurseDayShifts(weeks, nurseInShift, date, showActual);
				await setNurseTrainingStatus({ shifts, shiftsActual, date, shiftType, trainingStatus: newTrainingStatus });
				updateNurseDayTrainingStatus(date, nurseInShift.nurseId, newTrainingStatus, isDraftMode);
			} catch (e) {
				showErrorSnackbar("Failed to update training status", { nurseId: nurseInShift.id }, e);
				return false;
			}
			return true;
		},
		[
			isSaving,
			loadWeeks,
			t,
			showActual,
			analyticsSource,
			shiftType,
			date,
			nurseInShift,
			setNurseTrainingStatus,
			updateNurseDayTrainingStatus,
			isDraftMode,
			showErrorSnackbar,
		],
	);
};

/**
 * Charge and training-status (Trainee / Orientation / Education) toggles for one shift: the write, the
 * local schedule update and the analytics, shared by the desktop nurse menu and the mobile
 * staff-row sheet. Each action resolves `true` once the change is written, `false` when it was
 * skipped (a write already in flight) or failed — a failure is already shown in the error snackbar.
 * The training toggles write to all of the day's shifts, so a click first loads the day when it isn't in yet; they count
 * as saving from then on, and are never held back before the click.
 */
export const useShiftFlagActions = (target: ShiftFlagTarget) => {
	const { isLoading, toggleNurseCharge, setNurseTrainingStatus } = useHandleNurseChange();
	const shiftDay = useShiftDayWeeks(target.date);
	const isTrainingStatusSaving = isLoading.setNurseTrainingStatus || shiftDay.isLoadingForAction;
	const toggleCharge = useChargeToggle(target, toggleNurseCharge, isLoading.toggleNurseCharge);
	const setTrainingStatus = useTrainingStatusSetter(target, shiftDay.loadWeeks, setNurseTrainingStatus, isTrainingStatusSaving);
	const currentTrainingStatus = target.nurseInShift.trainingStatus;

	/** Sets `flag`, replacing any other training status — or clears it back to Regular when it is already set. */
	const toggleTrainingStatus = useCallback(
		(flag: ShiftTrainingFlag) => setTrainingStatus(currentTrainingStatus === flag ? NurseTrainingStatus.Regular : flag),
		[currentTrainingStatus, setTrainingStatus],
	);

	return {
		toggleCharge,
		toggleTrainingStatus,
		isChargeSaving: isLoading.toggleNurseCharge,
		isTrainingStatusSaving,
	};
};
